import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";

import { cn } from "@/lib/utils";

interface FilterPopoverProps {
  label: string;
  /** When > 0, shown as a badge after the label and the trigger gets the active style. */
  activeCount?: number;
  /** Optional right-aligned action when popover is open (e.g. "Limpiar"). */
  onClear?: () => void;
  children: ReactNode;
  /** Custom width for the popover panel. Default 240px. */
  width?: number;
}

/**
 * Compact, dependency-free filter popover. Used for Status/Channel/Tags
 * filter dropdowns in the inbox header. Closes on outside click and Escape.
 */
export function FilterPopover({
  label,
  activeCount = 0,
  onClear,
  children,
  width = 240,
}: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = activeCount > 0;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    }
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
          isActive
            ? "border-brand/40 bg-brand/10 text-brand"
            : "border-border bg-surface-2 text-muted-foreground hover:border-border-strong hover:text-foreground",
          open && !isActive && "border-border-strong text-foreground"
        )}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded-full bg-brand/20 px-1.5 py-0 text-[10px] tabular-nums">
            {activeCount}
          </span>
        )}
        <ChevronDown
          className={cn(
            "size-3 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-20 mt-1.5 overflow-hidden rounded-lg border border-border-strong bg-surface shadow-2xl ring-1 ring-white/5 animate-slide-up"
          style={{ width }}
        >
          {(label || onClear) && isActive && onClear && (
            <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {activeCount} {activeCount === 1 ? "activo" : "activos"}
              </span>
              <button
                onClick={() => {
                  onClear();
                }}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              >
                <X className="size-2.5" />
                Limpiar
              </button>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto p-1">{children}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Single row inside a FilterPopover. Renders a checkbox-like indicator,
 * the row label, and an optional right-side count.
 */
interface FilterRowProps {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  /** Right-side number (e.g. number of conversations matching). */
  count?: number;
  /** Optional inline icon/dot before the label. */
  prefix?: ReactNode;
}

export function FilterRow({
  selected,
  onClick,
  children,
  count,
  prefix,
}: FilterRowProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
        selected
          ? "bg-brand/10 text-foreground"
          : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
      )}
    >
      {/* Check indicator */}
      <span
        className={cn(
          "flex size-3.5 shrink-0 items-center justify-center rounded border",
          selected
            ? "border-brand bg-brand text-brand-foreground"
            : "border-border-strong"
        )}
      >
        {selected && (
          <svg viewBox="0 0 12 12" className="size-2.5" fill="none">
            <path
              d="M2 6.5L4.5 9L10 3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>

      {prefix}
      <span className="min-w-0 flex-1 truncate">{children}</span>

      {count !== undefined && (
        <span
          className={cn(
            "shrink-0 rounded px-1 text-[10px] tabular-nums",
            selected ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
